import { makeAutoObservable, runInAction } from 'mobx'
import { api } from '@/shared/api/endpoints'
import type { AuditEntryResponse, OnchainTxResponse } from '@/shared/api/types'
import { explain } from '@/shared/lib/errors'
import { Resource } from '@/stores/lib/Resource'
import type { UiStore } from '@/stores/UiStore'

const PAGE = 20

export type OpsTab = 'unmatched' | 'audit' | 'release'

/**
 * Операционная часть админки: несопоставленные поступления, журнал
 * действий и снятие закрепления с застрявшего вывода.
 *
 * Экранов этих в макете нет, но ручки есть, и без них шлюз нечем
 * обслуживать. Поступление, не привязавшееся ни к одной заявке, —
 * это лежащие в базе чужие деньги: узнать о них можно только по
 * жалобе плательщика, а закрыть вопрос — только сторно.
 */
export class AdminOpsStore {
  tab: OpsTab = 'unmatched'

  readonly unmatched: Resource<{ items: OnchainTxResponse[]; total: number }>
  readonly audit: Resource<{ items: AuditEntryResponse[]; total: number }>
  unmatchedOffset = 0
  auditOffset = 0

  /** Поступление, по которому спрашиваем сторно. */
  orphaning: OnchainTxResponse | null = null
  txid = ''
  reason = ''
  errors: { txid?: string; reason?: string; id?: string } = {}
  busy = false

  /** Снятие закрепления: ввод целиком руками, списка для него нет. */
  pinId = ''

  private readonly ui: UiStore

  constructor(ui: UiStore) {
    this.ui = ui
    this.unmatched = new Resource(async () => {
      const d = await api.admin.unmatchedDeposits({ limit: PAGE, offset: this.unmatchedOffset })
      return { items: d.items, total: d.total }
    })
    this.audit = new Resource(async () => {
      const d = await api.admin.audit({ limit: PAGE, offset: this.auditOffset })
      return { items: d.items, total: d.total }
    })
    makeAutoObservable<this, 'ui'>(this, { ui: false })
  }

  get limit(): number {
    return PAGE
  }

  setTab(tab: OpsTab): void {
    this.tab = tab
    if (tab === 'unmatched' && !this.unmatched.loaded) void this.unmatched.load()
    if (tab === 'audit' && !this.audit.loaded) void this.audit.load()
  }

  goUnmatched(offset: number): void {
    this.unmatchedOffset = offset
    void this.unmatched.load()
  }

  goAudit(offset: number): void {
    this.auditOffset = offset
    void this.audit.load()
  }

  // ---------- сторно поступления ----------

  askOrphan(tx: OnchainTxResponse): void {
    this.orphaning = tx
    this.txid = ''
    this.reason = ''
    this.errors = {}
  }

  close(): void {
    if (this.busy) return
    this.orphaning = null
    this.errors = {}
  }

  setTxid(value: string): void {
    this.txid = value.trim()
    const { txid: _drop, ...rest } = this.errors
    this.errors = rest
  }

  setReason(value: string): void {
    this.reason = value
    const { reason: _drop, ...rest } = this.errors
    this.errors = rest
  }

  setPinId(value: string): void {
    this.pinId = value.trim()
    const { id: _drop, ...rest } = this.errors
    this.errors = rest
  }

  /** Общая проверка для обеих опасных операций. Требования — с бекенда. */
  private checkDangerous(): boolean {
    const errors: { txid?: string; reason?: string } = {}
    if (this.txid.length < 16) errors.txid = 'TxID целиком, не короче 16 символов'
    if (this.reason.trim().length < 8) errors.reason = 'Причина не короче 8 символов'
    this.errors = { ...this.errors, ...errors }
    return !errors.txid && !errors.reason
  }

  async orphan(): Promise<void> {
    const tx = this.orphaning
    if (tx === null || this.busy || !this.checkDangerous()) return
    this.busy = true
    try {
      await api.admin.orphanDeposit(tx.id, this.txid, this.reason.trim())
      await this.unmatched.load()
      runInAction(() => { this.orphaning = null })
      this.ui.success('Поступление сторнировано')
    } catch (cause) {
      this.ui.failure(explain(cause))
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  async releasePin(): Promise<void> {
    if (this.busy) return
    const errors: { id?: string } = {}
    if (this.pinId.trim() === '') errors.id = 'Укажите номер заявки'
    this.errors = { ...this.errors, ...errors }
    if (errors.id || !this.checkDangerous()) return

    this.busy = true
    try {
      await api.admin.releasePin(this.pinId.trim(), this.txid, this.reason.trim())
      runInAction(() => { this.pinId = ''; this.txid = ''; this.reason = '' })
      this.ui.success('Закрепление снято')
      await this.audit.load()
    } catch (cause) {
      this.ui.failure(explain(cause))
    } finally {
      runInAction(() => { this.busy = false })
    }
  }
}
